import { SectionHeaderWithPagination } from '@intigral/ui/components/SectionHeaderWithPagination';
import { Carousel2 } from '@intigral/ui';
import { Button, Text, HeroBlock, FontIcon, DetailedPoster } from '@intigral/shared';
import withResponsiveStyles from '@intigral/shared/components/hocs/ResponiseStyles/withResponsiveStyles';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { STCPlayGames } from '@intigral/domain/lib/entity/interface/iBigScreenCmsMoods';
import h from '../../helpers/common';
import { __ } from '../../translations';
import { useContext, useEffect, useState } from 'react';
import { MixpanelContext } from '../../contexts/MixpanelContext';
import { EVENT } from '../../contexts/MixpanelConstants';
import { useQuery } from '@tanstack/react-query';
import { DI } from '@intigral/constant/config/DIContainer';
import { container } from '@intigral/domain/lib/DataDomain';
import { ICustomConfigurationService } from '@intigral/domain/lib/application/services/CustomConfigurationService';
import url from '../../helpers/url';
import { Modal } from '../ui/Modal';

const StyleButton = withResponsiveStyles(Button);

export default function GamePopup() {
    const { track }: { track: any } = useContext(MixpanelContext);
    const [searchParams] = useSearchParams();
    const [similarGames, setSimilarGames] = useState([]);
    const [gameData, setGameData] = useState(null);
    const [isPopupOpen, setPopupOpen] = useState(false);
    const navigate = useNavigate();
    const gameKey = searchParams?.get('game');

    const { isLoading, data: gamesData } = useQuery({
        queryKey: ['games'],
        enabled: gameKey ? true : false,
        queryFn: async () => {
            try {
                const commonService = container.resolve<ICustomConfigurationService>(
                    DI.SERVICES.ICustomConfigurationService
                );
                const response = await commonService.getGames();
                return response?.data || [];
            } catch (e) {
                return [];
            }
        },
    });

    useEffect(() => {
        if (gameData?.id) {
            track(EVENT.STC_PLAY_GAMES_CAROUSEL.GAME_DETAIL, {
                'Game Name': gameData?.name_en,
            });
        }
    }, [gameData?.id]);

    useEffect(() => {
        if (gameKey) {
            const gameId = url.decodeValue(searchParams.get('game'));
            setSelectedGamesData(gameId);
        }
    }, [gameKey, gamesData]);

    const handlePlay = (gameData: STCPlayGames) => {
        track(EVENT.STC_PLAY_GAMES_CAROUSEL.GAME_DETAIL_PLAY, {
            'Game Name': gameData?.name_en,
        });
    };

    const handleClosePopup = () => {
        setPopupOpen(false);
        setSimilarGames([]);
        setGameData(null);
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('game');
        navigate(`?${newParams.toString()}`);
    };

    const setSelectedGamesData = (gameId) => {
        const filteredData = gamesData?.reduce(
            (acc, item) => {
                if (item.id?.toString() === gameId?.toString()) {
                    acc.gameData = item;
                } else {
                    acc.similarData.push(item);
                }
                return acc;
            },
            { gameData: null, similarData: [] }
        );
        const gameData = filteredData?.gameData ?? null;
        const similarData = filteredData?.similarData ?? [];
        setSimilarGames(similarData);
        setGameData(gameData);
        if (gameData) {
            setPopupOpen(true);
        }
    };

    const handleCardClick = (gameData: STCPlayGames, isDetailPage = false) => {
        if (gameData?.id) {
            setSelectedGamesData(gameData.id);
            // const newParams = new URLSearchParams(searchParams);
            // newParams.set('game', btoa(gameData?.id?.toString()));
            // navigate(`?${newParams.toString()}`);
            track(EVENT.STC_PLAY_GAMES_CAROUSEL.ITEM_CLICK, {
                'Screen Name': !isDetailPage ? h.getScreen() : 'STC Play Game Details',
                'Carousel Name': 'Games from stc play',
                'Carousel Type': 'STC Play Games',
                'Item Name': gameData?.name_en,
            });
        }
    };

    return (
        <>
            <Modal useFlexLayout={false} open={isPopupOpen} onOpenChange={handleClosePopup}>
                <HeroBlock
                    heading={
                        <Text as="h2" variant="h2" margin="4x">
                            {gameData?.[`name_${h.getLang()}`]}
                        </Text>
                    }
                    picture={gameData?.img}
                    compact
                    detail
                    blurred
                    isLoading={isLoading}
                >
                    <StyleButton
                        compact
                        marginBottom="6x"
                        placement={{ default: 'none', md: 'center' }}
                        LinkComponent={Link}
                        to={gameData?.deeplink_url}
                        rel="noopener noreferrer"
                        target="_blank"
                        onClick={(e) => {
                            e?.stopPropagation();
                            handlePlay(gameData);
                        }}
                    >
                        <span>{__('stc_play_games_play_now')}</span>{' '}
                        <FontIcon name="play-outline" size="smaller" md="smaller" />
                    </StyleButton>
                    <Text as="p" color="gray">
                        {h.getLang() === 'ar' ? gameData?.description_ar : gameData?.description_en}
                    </Text>
                </HeroBlock>
                {(similarGames?.length > 0 && (
                    <>
                        <SectionHeaderWithPagination title={__('stc_play_games_similar_games')} className="paging" />
                        <Carousel2
                            swiperSettings={{
                                autoplay: false,
                                loop: false,
                                dir: h.getLang() == 'ar' ? 'rtl' : 'ltr',
                                slidesPerGroup: 1,
                                slidesPerView: 2,
                                breakpoints: {
                                    560: {
                                        slidesPerView: 3,
                                    },
                                },
                                pagination: {
                                    el: 'paging',
                                    clickable: true,
                                },
                            }}
                        >
                            {similarGames?.map((item, index) => (
                                <DetailedPoster
                                    key={`games-detail-${index}-${item.id}`}
                                    picture={item.img}
                                    heading={
                                        <Text
                                            as="h4"
                                            variant="h4"
                                            alignment="center"
                                            margin="0"
                                            size={{ default: '14', md: '22' }}
                                        >
                                            {h.getLang() === 'ar' ? item.name_ar : item.name_en}
                                        </Text>
                                    }
                                    onClick={() => {
                                        handleCardClick(item, true);
                                    }}
                                />
                            ))}
                        </Carousel2>
                    </>
                )) || <></>}
            </Modal>
        </>
    );
}
